/**
 * Cache TTL simples em memória — evita chamadas desnecessárias à Football
 * Data API (item 17 do escopo desta etapa). Suficiente pro estágio atual;
 * não é uma solução de cache distribuído (isso viria com Cloud Functions +
 * Firestore como cache de nível 1, já desenhado na arquitetura original,
 * seção 11 do documento base).
 */

interface CacheEntry<T> {
  value: T;
  expiresAt: number;
}

const store = new Map<string, CacheEntry<unknown>>();

export function cacheGet<T>(key: string): T | undefined {
  const entry = store.get(key);
  if (!entry) return undefined;
  if (Date.now() > entry.expiresAt) {
    store.delete(key);
    return undefined;
  }
  return entry.value as T;
}

export function cacheSet<T>(key: string, value: T, ttlMs: number): void {
  store.set(key, { value, expiresAt: Date.now() + ttlMs });
}

export function cacheClear(): void {
  store.clear();
}

/** TTLs recomendados por tipo de dado (seção 17 do escopo). */
export const CACHE_TTL = {
  competitions: 24 * 60 * 60 * 1000, // 24h — muda raramente
  clubs: 12 * 60 * 60 * 1000, // 12h
  matchesUpcoming: 60 * 60 * 1000, // 1h — só quando NENHUM jogo do lote está perto de começar
  matchesLive: 60 * 1000, // 1min — enquanto houver jogo ao vivo ou prestes a começar na rodada
} as const;

/**
 * Decide o TTL certo pra uma lista de partidas: se qualquer uma delas já
 * começou (pelo horário) mas ainda não foi marcada como "finished", ou vai
 * começar nas próximas horas, o cache tem que ser curto — senão a tela fica
 * mostrando "Agendada" e o campo de palpite liberado bem depois do jogo já
 * ter começado de verdade (foi exatamente o bug relatado: Grêmio x São
 * Paulo começou 16h e o palpite continuou editável).
 */
export function ttlForMatches(kickoffTimesIso: string[], anyLive: boolean, hoursAheadThreshold = 3): number {
  if (anyLive) return CACHE_TTL.matchesLive;
  const now = Date.now();
  const isAnyImminentOrOverdue = kickoffTimesIso.some((iso) => {
    const kickoff = new Date(iso).getTime();
    const hoursUntil = (kickoff - now) / (60 * 60 * 1000);
    return hoursUntil <= hoursAheadThreshold; // inclui negativo = já deveria ter começado
  });
  return isAnyImminentOrOverdue ? CACHE_TTL.matchesLive : CACHE_TTL.matchesUpcoming;
}
